/* Original, dependency-free CamGuard enhancement. The existing checker is the
 * only compatibility authority. No model-name matching, copied data or network.
 * Animated Beam informed the connecting-line concept; no component code copied. */
(() => {
  'use strict';
  const root = document.getElementById('cgre-page');
  if (!root || root.dataset.cgmReady) return;
  const select = root.querySelector('#cc-devsel');
  const result = root.querySelector('#cc-devout');
  const panel = root.querySelector('#cc-view-device');
  if (!select || !result || !panel) return;
  root.dataset.cgmReady = 'true';

  const ns = 'http://www.w3.org/2000/svg';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const known = new Map([
    ['● Needs a hub: this one runs it', ['required', 'Hub required', '●']],
    ['✓ Works (optional)', ['supported', 'Works (optional)', '✓']],
    ["✓* Listed as compatible by eufy, but eufy's own pages disagree; ask us first", ['conflict', 'Conflicting sources', '!']],
    ['✕ Not compatible', ['no', 'Not compatible', '×']],
    ['? Not published by eufy: ask us', ['unknown', 'Not published', '?']],
    ["— Doesn't apply", ['na', "Doesn't apply", '—']],
    ['… Checking', ['unknown', 'Checking', '?']]
  ]);
  const make = (tag, cls, text) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text) el.textContent = text;
    return el;
  };
  const map = make('section', 'cgm-map');
  map.setAttribute('aria-label', 'Device to HomeBase connection map');
  map.dataset.empty = 'true';
  const eyebrow = make('p', 'cgm-eyebrow', 'Connection map');
  const title = make('h3', 'cgm-title', 'Find your connection.');
  const subtitle = make('p', 'cgm-subtitle', 'Choose a device above to see its published HomeBase pairings.');
  const stage = make('div', 'cgm-stage');
  const wires = document.createElementNS(ns, 'svg');
  wires.classList.add('cgm-wires');
  wires.setAttribute('aria-hidden', 'true');
  wires.setAttribute('focusable', 'false');
  const source = make('div', 'cgm-source');
  const hardware = make('div', 'cgm-hardware');
  hardware.setAttribute('aria-hidden', 'true');
  const sourceLabel = make('p', 'cgm-source-label');
  sourceLabel.append(make('strong', '', 'Your device'), document.createTextNode('Illustrative schematic'));
  source.append(hardware, sourceLabel);
  const hubs = make('div', 'cgm-hubs');
  hubs.hidden = true;
  const detail = make('p', 'cgm-detail', 'Your checker answer and any limitations will appear below.');
  detail.id = 'cgm-connection-detail';
  detail.setAttribute('role', 'status');
  detail.setAttribute('aria-live', 'polite');
  detail.setAttribute('aria-atomic', 'true');
  const footnote = make('p', 'cgm-footnote', 'A connection here describes compatibility, not a live pairing or a signal test.');
  stage.append(wires, source, hubs);
  map.append(eyebrow, title, subtitle, stage, detail, footnote);
  result.before(map);

  let rows = [];
  let signature = '';
  let frame = 0;
  let pendingBeam = false;
  let visible = false;
  let animations = [];
  let paths = [];
  let active = -1;

  function stopBeams() {
    animations.forEach(animation => animation.cancel());
    animations = [];
  }

  function canMove() {
    return visible && !document.hidden && !panel.hidden && !reduced.matches;
  }

  function syncMotion() {
    if (reduced.matches) {
      pendingBeam = false;
      stopBeams();
      return;
    }
    if (!canMove()) {
      animations.forEach(animation => {
        if (animation.playState === 'running') animation.pause();
      });
      return;
    }
    animations.forEach(animation => {
      if (animation.playState === 'paused') animation.play();
    });
    if (!pendingBeam || !paths.length) return;
    pendingBeam = false;
    paths.forEach(({ pulse, state }, index) => {
      if (!pulse.animate || (state !== 'supported' && state !== 'required')) return;
      const animation = pulse.animate([
        { strokeDashoffset: '7', opacity: 0, offset: 0 },
        { opacity: 1, offset: .08 },
        { opacity: 1, offset: .82 },
        { strokeDashoffset: '-100', opacity: 0, offset: 1 }
      ], { duration: 1050, delay: index * 85, iterations: 1, easing: 'ease-in-out' });
      animations.push(animation);
      animation.onfinish = () => {
        animations = animations.filter(item => item !== animation);
      };
    });
  }

  function showRow(index) {
    active = index;
    rows.forEach((row, i) => row.button.setAttribute('aria-pressed', String(i === index)));
    paths.forEach((path, i) => path.route.setAttribute('data-active', String(i === index)));
    const row = rows[index];
    detail.textContent = `${row.name}: ${row.exact}. Read the checker notes below for the conditions of this pairing.`;
  }

  function draw() {
    frame = 0;
    if (!rows.length || panel.hidden || !stage.clientWidth) return;
    const bounds = stage.getBoundingClientRect();
    const device = hardware.getBoundingClientRect();
    const vertical = window.matchMedia('(max-width: 520px)').matches;
    wires.setAttribute('viewBox', `0 0 ${bounds.width} ${bounds.height}`);
    // Keep path elements during resize so a selection does not restart its beam.
    rows.forEach((row, i) => {
      const target = row.button.getBoundingClientRect();
      const x1 = (vertical ? device.left + device.width / 2 : device.right) - bounds.left;
      const y1 = (vertical ? device.bottom : device.top + device.height / 2) - bounds.top;
      const x2 = (vertical ? target.left + target.width / 2 : target.left) - bounds.left;
      const y2 = (vertical ? target.top : target.top + target.height / 2) - bounds.top;
      const bend = vertical ? (y1 + y2) / 2 : (x1 + x2) / 2;
      const d = vertical
        ? `M${x1},${y1} C${x1},${bend} ${x2},${bend} ${x2},${y2}`
        : `M${x1},${y1} C${bend},${y1} ${bend},${y2} ${x2},${y2}`;
      if (!paths[i]) {
        const route = document.createElementNS(ns, 'path');
        route.classList.add('cgm-route');
        route.dataset.state = row.state;
        const pulse = document.createElementNS(ns, 'path');
        pulse.classList.add('cgm-pulse');
        pulse.setAttribute('pathLength', '100');
        wires.append(route, pulse);
        paths[i] = { route, pulse, state: row.state };
      }
      paths[i].route.setAttribute('d', d);
      paths[i].route.setAttribute('data-active', String(i === active));
      paths[i].pulse.setAttribute('d', d);
    });
    syncMotion();
  }

  function queueDraw() {
    if (!frame) frame = window.requestAnimationFrame(draw);
  }

  function update() {
    const option = select.options[select.selectedIndex];
    const chosen = select.value !== '' && !result.hidden;
    const chips = chosen ? [...result.querySelectorAll('.cc-chip')] : [];
    const nextSignature = chosen ? `${select.value}|${result.textContent}` : '';
    if (nextSignature === signature) return;
    signature = nextSignature;
    stopBeams();
    paths = [];
    rows = [];
    active = -1;
    wires.replaceChildren();
    hubs.replaceChildren();
    pendingBeam = chosen && !reduced.matches;
    title.textContent = chosen ? option.textContent.trim() : 'Find your connection.';
    const headline = result.querySelector('.cc-headline');
    subtitle.textContent = chosen && headline ? headline.textContent : 'Choose a device above to see its published HomeBase pairings.';
    map.dataset.empty = String(!chips.length);
    hubs.hidden = !chips.length;
    sourceLabel.firstElementChild.textContent = chosen ? 'Selected device' : 'Your device';
    detail.textContent = chips.length
      ? 'Select a HomeBase to inspect the pairing. Use Tab then Enter with a keyboard. Dashed lines are not confirmed connections.'
      : chosen
        ? 'No HomeBase connection is drawn for this result. The original checker answer and product notes below explain the setup.'
        : 'Your checker answer and any limitations will appear below.';

    chips.forEach(chip => {
      const name = chip.querySelector('.n');
      const status = chip.querySelector('.s');
      if (!name || !status) return;
      const published = status.textContent.trim().replace(/\s+/g, ' ');
      // Unknown future labels remain unknown. Never infer support from class .ok,
      // product names, a checkmark alone, or the colour of the existing chip.
      const [state, short, symbol] = known.get(published) || ['unknown', 'Read checker answer', '?'];
      const exact = published.replace(/^[●✓✕?—…]+\*?\s+/, '');
      const button = make('button', 'cgm-hub');
      button.type = 'button';
      button.dataset.state = state;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `${name.textContent}: ${exact}`);
      const icon = make('span', 'cgm-hub-icon');
      icon.setAttribute('aria-hidden', 'true');
      const label = make('span');
      label.append(make('span', 'cgm-hub-name', name.textContent), make('span', 'cgm-hub-status', short));
      const badge = make('span', 'cgm-status-icon', symbol);
      badge.setAttribute('aria-hidden', 'true');
      button.append(icon, label, badge);
      const index = rows.length;
      rows.push({ name: name.textContent, state, exact, button });
      button.addEventListener('click', () => showRow(index));
      hubs.append(button);
    });
    queueDraw();
  }

  function viewport() {
    root.style.setProperty('--cgm-viewport', `${document.documentElement.clientWidth}px`);
    queueDraw();
  }
  // Observing the result survives the checker's own rendering timing and hash
  // selections without patching its script or dispatching synthetic changes.
  const resultObserver = new MutationObserver(update);
  resultObserver.observe(result, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
  const tabObserver = new MutationObserver(() => { queueDraw(); syncMotion(); });
  tabObserver.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
  if ('ResizeObserver' in window) {
    const resize = new ResizeObserver(queueDraw);
    resize.observe(stage);
  }
  if ('IntersectionObserver' in window) {
    const intersection = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      syncMotion();
    }, { threshold: .1 });
    intersection.observe(map);
  } else {
    // Static, fully readable diagram for older browsers; no unobserved motion.
    visible = false;
  }
  window.addEventListener('resize', viewport, { passive: true });
  document.addEventListener('visibilitychange', syncMotion);
  reduced.addEventListener('change', syncMotion);
  viewport();
  update();
})();
